import { Helmet } from 'react-helmet-async';
import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  getFirestore,
  collection,
  getDocs,
  query,
  where,
  doc,
  addDoc,
  updateDoc,
  setDoc,
  getDoc,
  deleteDoc,
  serverTimestamp,
  orderBy,
} from 'firebase/firestore';
import axios from 'axios';
import Iconify from '../components/iconify';
import Scrollbar from '../components/scrollbar';
import SettlementsAdmin from '../sections/partners/SettlementsAdmin';
import firebaseApp from '../utils/firebaseConfig';
import { hasPermission, PERMISSIONS } from '../protectedRoute';
import { isPrivateCourse } from '../utils/privateCourse';
import { openPartnerInvoice } from '../utils/partnerInvoice';
import {
  PARTNER_PORTAL_PATH,
  PARTNER_CHANGE_HOURS,
  money,
  toCents,
  currentMonthKey,
  monthLabel,
  recentMonthKeys,
  partnerRowStatus,
  partnerBillingOf,
  partnerCommissionOf,
  partnerPenaltyOf,
  suggestedPartnerPriceCents,
  summarizePartnerRows,
  settlementId,
  settlementStatus,
  formatDueDate,
  dueDateFor,
  DEFAULT_PAYMENT_TERM_DAYS,
} from '../utils/partners';

const db = getFirestore(firebaseApp);
const FUNCTIONS_BASE = 'https://us-central1-bb-target-center.cloudfunctions.net';

const emptyForm = {
  name: '',
  code: '',
  contactName: '',
  phone: '',
  email: '',
  pin: '',
  commission: '5.00',
  penalty: '20.00',
  defaultPrice: '',
  byCategory: {},
  active: true,
  notes: '',
  paymentTermDays: String(DEFAULT_PAYMENT_TERM_DAYS),
};

const genCode = (name) => {
  const base = (name || 'ALI').normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^A-Za-z]/g, '').toUpperCase().slice(0, 4) || 'ALI';
  return `${base}-${Math.floor(100 + Math.random() * 900)}`;
};

const formatTime = (t) => {
  if (!t) return '';
  const [h, m] = t.split(':');
  const hour = parseInt(h, 10);
  return `${hour % 12 || 12}:${m} ${hour >= 12 ? 'PM' : 'AM'}`;
};

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(`${iso}T12:00:00`);
  return d.toLocaleDateString('es-PR', { weekday: 'short', day: 'numeric', month: 'short' });
};

export default function PartnersPage() {
  const canEdit = hasPermission(PERMISSIONS.EDIT_PAYMENTS);
  const [tab, setTab] = useState(0);
  const [partners, setPartners] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  // Diálogo crear/editar
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  // Estado de cuenta
  const [stPartnerId, setStPartnerId] = useState('');
  const [stMonth, setStMonth] = useState(currentMonthKey());
  const [stRows, setStRows] = useState([]);
  const [stLoading, setStLoading] = useState(false);
  const [settlement, setSettlement] = useState(null);
  const [settleNote, setSettleNote] = useState('');
  const [settleSaving, setSettleSaving] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [pSnap, cSnap] = await Promise.all([
        getDocs(query(collection(db, 'partners'), orderBy('name'))),
        getDocs(query(collection(db, 'reservation_categories'), where('type', '==', 'course'))),
      ]);
      setPartners(pSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      // El curso privado no se vende por el portal (se coordina con BBTC):
      // no lleva precio de aliado.
      setCourses(cSnap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((c) => c.active !== false && !isPrivateCourse(c)));
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  useEffect(() => { loadAll(); }, []);

  const openCreate = () => {
    setEditingId(null);
    // Tarifas de la V3: $65 renovación y $85 licencia nueva (se pueden cambiar)
    const byCategory = {};
    courses.forEach((c) => {
      const cents = suggestedPartnerPriceCents(c.name);
      if (cents) byCategory[c.id] = (cents / 100).toFixed(2);
    });
    setForm({ ...emptyForm, code: '', byCategory });
    setFormError('');
    setDialogOpen(true);
  };

  const openEdit = (p) => {
    setEditingId(p.id);
    const byCategory = {};
    Object.entries((p.pricing && p.pricing.byCategory) || {}).forEach(([k, v]) => { byCategory[k] = (Number(v) / 100).toFixed(2); });
    setForm({
      name: p.name || '',
      code: p.code || '',
      contactName: p.contactName || '',
      phone: p.phone || '',
      email: p.email || '',
      pin: '', // el PIN vive en partner_private; vacío = no cambiarlo
      commission: (partnerCommissionOf(p) / 100).toFixed(2),
      penalty: (partnerPenaltyOf(p) / 100).toFixed(2),
      defaultPrice: p.pricing && p.pricing.defaultCents != null ? (Number(p.pricing.defaultCents) / 100).toFixed(2) : '',
      byCategory,
      active: p.active !== false,
      notes: p.notes || '',
      paymentTermDays: String(p.paymentTermDays || DEFAULT_PAYMENT_TERM_DAYS),
    });
    setFormError('');
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const name = form.name.trim();
    const phoneDigits = form.phone.replace(/\D/g, '');
    if (!name) { setFormError('Escribe el nombre de la armería.'); return; }
    if (phoneDigits.length < 10) { setFormError('El teléfono debe tener 10 dígitos (es con lo que entran al portal).'); return; }
    if ((!editingId || form.pin) && !/^\d{4}$/.test(form.pin)) { setFormError('El PIN debe ser de 4 dígitos.'); return; }
    const termDays = parseInt(form.paymentTermDays, 10);
    if (!Number.isFinite(termDays) || termDays < 1 || termDays > 60) { setFormError('Los días para pagar deben estar entre 1 y 60 (el acuerdo dice 5).'); return; }
    if (!form.defaultPrice.trim() && Object.values(form.byCategory).every((v) => !String(v || '').trim())) {
      setFormError('Pon el precio por curso que le cobras a este aliado (general o por curso).');
      return;
    }
    setSaving(true);
    setFormError('');
    try {
      const byCategory = {};
      Object.entries(form.byCategory).forEach(([k, v]) => { if (String(v || '').trim()) byCategory[k] = toCents(v); });
      const data = {
        name,
        code: (form.code.trim() || genCode(name)).toUpperCase(),
        contactName: form.contactName.trim(),
        phone: phoneDigits,
        phoneDigits,
        email: form.email.trim().toLowerCase(),
        commissionCents: toCents(form.commission),
        penaltyCents: toCents(form.penalty),
        pricing: { defaultCents: form.defaultPrice.trim() ? toCents(form.defaultPrice) : 0, byCategory },
        active: form.active,
        notes: form.notes.trim(),
        paymentTermDays: termDays,
        updatedAt: serverTimestamp(),
      };
      let partnerId = editingId;
      if (editingId) {
        await updateDoc(doc(db, 'partners', editingId), data);
      } else {
        const created = await addDoc(collection(db, 'partners'), { ...data, createdAt: serverTimestamp() });
        partnerId = created.id;
      }
      // El PIN no se guarda en partners (era legible sin auth): va a
      // partner_private por la function adminSetPartnerPin.
      if (form.pin) await axios.post(`${FUNCTIONS_BASE}/adminSetPartnerPin`, { partnerId, pin: form.pin });
      setDialogOpen(false);
      loadAll();
    } catch (e) {
      console.error(e);
      setFormError(`No se pudo guardar: ${e.message}`);
    }
    setSaving(false);
  };

  const toggleActive = async (p) => {
    await updateDoc(doc(db, 'partners', p.id), { active: p.active === false, updatedAt: serverTimestamp() });
    loadAll();
  };

  const toggleSuspended = async (p) => {
    const next = !p.suspendedForNonPayment;
    if (!window.confirm(next
      ? `¿Suspender las inscripciones de "${p.name}" por balance vencido? Podrá entrar al portal y pagar, pero no inscribir. Los términos piden avisarle por escrito.`
      : `¿Reactivar las inscripciones de "${p.name}"?`)) return;
    await updateDoc(doc(db, 'partners', p.id), { suspendedForNonPayment: next, suspendedAt: next ? serverTimestamp() : null, updatedAt: serverTimestamp() });
    loadAll();
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`¿Eliminar el aliado "${p.name}"? Sus reservaciones pasadas se conservan.`)) return;
    await deleteDoc(doc(db, 'partners', p.id));
    loadAll();
  };

  // ---------- Estado de cuenta ----------
  const loadStatement = async () => {
    if (!stPartnerId || !stMonth) { setStRows([]); setSettlement(null); return; }
    setStLoading(true);
    try {
      const snap = await getDocs(query(collection(db, 'reservations'), where('partnerId', '==', stPartnerId)));
      const rows = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((r) => (r.date || '').startsWith(stMonth))
        .sort((a, b) => (a.date + a.startTime).localeCompare(b.date + b.startTime));
      setStRows(rows);
      const sDoc = await getDoc(doc(db, 'partner_settlements', settlementId(stPartnerId, stMonth)));
      setSettlement(sDoc.exists() ? sDoc.data() : null);
      setSettleNote(sDoc.exists() ? (sDoc.data().note || '') : '');
    } catch (e) {
      console.error(e);
    }
    setStLoading(false);
  };

  useEffect(() => { loadStatement(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [stPartnerId, stMonth]);

  const summary = useMemo(() => summarizePartnerRows(stRows), [stRows]);
  const stPartner = partners.find((p) => p.id === stPartnerId);

  const markSettled = async (paid) => {
    if (!stPartnerId) return;
    setSettleSaving(true);
    try {
      const ref = doc(db, 'partner_settlements', settlementId(stPartnerId, stMonth));
      if (paid) {
        await setDoc(ref, {
          partnerId: stPartnerId,
          partnerName: stPartner ? stPartner.name : '',
          month: stMonth,
          attended: summary.attended,
          subtotalCents: summary.subtotalCents,
          commissionCents: summary.commissionCents,
          noShowPenalties: summary.noShowPenalties,
          lateCancelPenalties: summary.lateCancelPenalties,
          penaltyCents: summary.penaltyCents,
          totalCents: summary.totalCents,
          amountDueCents: 0,
          dueDate: dueDateFor(stMonth, stPartner ? stPartner.paymentTermDays : undefined),
          note: settleNote.trim(),
          paidNote: settleNote.trim(),
          paidAt: serverTimestamp(),
          paidVia: 'manual',
          closedBy: 'staff',
          status: 'paid',
        }, { merge: true });
      } else {
        await deleteDoc(ref);
      }
      await loadStatement();
    } catch (e) {
      console.error(e);
      alert(`No se pudo guardar: ${e.message}`);
    }
    setSettleSaving(false);
  };

  const portalUrl = `${window.location.origin}${PARTNER_PORTAL_PATH}`;

  return (
    <>
      <Helmet><title>Aliados | BBTC</title></Helmet>
      <Container maxWidth="xl">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
          <Box>
            <Typography variant="h4">Aliados (armerías)</Typography>
            <Typography variant="body2" color="text.secondary">
              Armerías que inscriben a sus clientes en los cursos. El cliente les paga a ellos; BBTC les factura a fin de mes.
            </Typography>
          </Box>
          {canEdit && tab === 0 && (
            <Button variant="contained" startIcon={<Iconify icon="eva:plus-fill" />} onClick={openCreate}>
              Nuevo aliado
            </Button>
          )}
        </Stack>

        <Alert severity="info" sx={{ mb: 2 }}>
          Portal del aliado: <strong>{portalUrl}</strong> — entran con su teléfono y PIN de 4 dígitos.
        </Alert>

        <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2 }}>
          <Tab label="Aliados" />
          <Tab label="Estados de cuenta" />
          <Tab label="Detalle del mes" />
        </Tabs>

        {tab === 1 && (
          <SettlementsAdmin partners={partners} canEdit={canEdit} onOpenDetail={(pid, m) => { setStPartnerId(pid); setStMonth(m); setTab(2); }} />
        )}

        {tab === 0 && (
          <Card>
            <Scrollbar>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Armería</TableCell>
                      <TableCell>Código</TableCell>
                      <TableCell>Contacto</TableCell>
                      <TableCell>Precio por curso</TableCell>
                      <TableCell>Comisión / penalidad</TableCell>
                      <TableCell>Cobro ACH</TableCell>
                      <TableCell>Estado</TableCell>
                      <TableCell align="right" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {!loading && partners.length === 0 && (
                      <TableRow><TableCell colSpan={8}><Typography color="text.secondary" sx={{ p: 2 }}>Todavía no hay aliados. Crea el primero con "Nuevo aliado".</Typography></TableCell></TableRow>
                    )}
                    {partners.map((p) => {
                      const byCat = (p.pricing && p.pricing.byCategory) || {};
                      return (
                        <TableRow key={p.id} hover>
                          <TableCell>
                            <Typography variant="subtitle2">{p.name}</Typography>
                            <Typography variant="caption" color="text.secondary">{p.phone}{p.email ? ` · ${p.email}` : ''}</Typography>
                          </TableCell>
                          <TableCell><Typography sx={{ fontFamily: 'monospace' }}>{p.code}</Typography></TableCell>
                          <TableCell>{p.contactName || '—'}</TableCell>
                          <TableCell>
                            <Typography variant="body2">General: {money((p.pricing && p.pricing.defaultCents) || 0)}</Typography>
                            {Object.entries(byCat).map(([cid, cents]) => {
                              const c = courses.find((x) => x.id === cid);
                              return <Typography key={cid} variant="caption" sx={{ display: 'block' }} color="text.secondary">{c ? c.name : cid}: {money(cents)}</Typography>;
                            })}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{money(partnerCommissionOf(p))} / referido</Typography>
                            <Typography variant="caption" color="text.secondary">Penalidad: {money(partnerPenaltyOf(p))}</Typography>
                          </TableCell>
                          <TableCell>
                            <Stack spacing={0.5} alignItems="flex-start">
                              {p.bankStatus === 'verified' && <Chip size="small" color="success" variant="outlined" label={p.autoDebit ? 'Banco · débito automático' : 'Banco verificado'} />}
                              {p.bankStatus === 'pending_verification' && <Chip size="small" color="warning" variant="outlined" label="Banco pendiente" />}
                              {p.bankStatus === 'errored' && <Chip size="small" color="error" variant="outlined" label="Banco con error" />}
                              {!p.bankStatus && <Typography variant="caption" color="text.secondary">Sin banco</Typography>}
                              <Typography variant="caption" color="text.secondary">
                                {p.termsAcceptedAt ? `Términos ${p.termsVersion || ''} aceptados` : 'Términos sin aceptar'} · paga en {p.paymentTermDays || DEFAULT_PAYMENT_TERM_DAYS} días
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Chip size="small" label={p.active === false ? 'Inactivo' : 'Activo'} color={p.active === false ? 'default' : 'success'} />
                            {p.suspendedForNonPayment && <Chip size="small" color="error" label="Suspendido por mora" sx={{ mt: 0.5 }} />}
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="Ver estado de cuenta"><IconButton onClick={() => { setStPartnerId(p.id); setTab(1); }}><Iconify icon="eva:file-text-outline" /></IconButton></Tooltip>
                            {canEdit && (
                              <>
                                <Tooltip title="Editar"><IconButton onClick={() => openEdit(p)}><Iconify icon="eva:edit-fill" /></IconButton></Tooltip>
                                <Tooltip title={p.suspendedForNonPayment ? 'Reactivar inscripciones' : 'Suspender inscripciones por mora'}><IconButton color={p.suspendedForNonPayment ? 'error' : 'default'} onClick={() => toggleSuspended(p)}><Iconify icon="eva:alert-triangle-outline" /></IconButton></Tooltip>
                                <Tooltip title={p.active === false ? 'Activar' : 'Desactivar'}><IconButton onClick={() => toggleActive(p)}><Iconify icon={p.active === false ? 'eva:checkmark-circle-2-outline' : 'eva:slash-outline'} /></IconButton></Tooltip>
                                <Tooltip title="Eliminar"><IconButton color="error" onClick={() => handleDelete(p)}><Iconify icon="eva:trash-2-outline" /></IconButton></Tooltip>
                              </>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Scrollbar>
          </Card>
        )}

        {tab === 2 && (
          <Card sx={{ p: 3 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 3 }}>
              <TextField select label="Aliado" value={stPartnerId} onChange={(e) => setStPartnerId(e.target.value)} sx={{ minWidth: 260 }}>
                {partners.map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
              </TextField>
              <TextField select label="Mes" value={stMonth} onChange={(e) => setStMonth(e.target.value)} sx={{ minWidth: 200 }}>
                {recentMonthKeys(18).map((k) => <MenuItem key={k} value={k}>{monthLabel(k)}</MenuItem>)}
              </TextField>
            </Stack>

            {!stPartnerId && <Typography color="text.secondary">Escoge un aliado para ver su estado de cuenta.</Typography>}

            {stPartnerId && (
              <>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  {[
                    ['Inscritos', summary.total],
                    ['Asistieron', summary.attended],
                    ['No se presentaron', summary.noShow],
                    ['Cancelados', summary.cancelled],
                    ['Próximos', summary.upcoming],
                  ].map(([l, v]) => (
                    <Grid item xs={6} md={2.4} key={l}>
                      <Card sx={{ p: 2, textAlign: 'center', bgcolor: 'grey.100' }}>
                        <Typography variant="h5">{v}</Typography>
                        <Typography variant="caption" color="text.secondary">{l}</Typography>
                      </Card>
                    </Grid>
                  ))}
                </Grid>

                <Card variant="outlined" sx={{ p: 2, mb: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>Estado de cuenta — {stPartner ? stPartner.name : ''} · {monthLabel(stMonth)}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Se factura cada curso al que el cliente asistió, menos la comisión por referido, más una penalidad por cada
                    inasistencia o cancelación con menos de {PARTNER_CHANGE_HOURS} horas (sin comisión). Las cancelaciones a tiempo valen $0.
                  </Typography>
                  {summary.courseLines.length === 0 && (
                    <Stack direction="row" justifyContent="space-between"><Typography>Cursos asistidos (0)</Typography><Typography sx={{ whiteSpace: 'nowrap', pl: 1 }}>{money(0)}</Typography></Stack>
                  )}
                  {summary.courseLines.map((l) => (
                    <Stack key={`${l.name}-${l.priceCents}`} direction="row" justifyContent="space-between">
                      <Typography>Asistieron a {l.name} ({l.count} × {money(l.priceCents)})</Typography>
                      <Typography sx={{ whiteSpace: 'nowrap', pl: 1 }}>{money(l.subtotalCents)}</Typography>
                    </Stack>
                  ))}
                  <Stack direction="row" justifyContent="space-between"><Typography>Menos comisión por referido ({summary.commissionLabel})</Typography><Typography sx={{ whiteSpace: 'nowrap', pl: 1 }}>− {money(summary.commissionCents)}</Typography></Stack>
                  <Stack direction="row" justifyContent="space-between"><Typography>Más penalidad por inasistencia ({summary.noShowPenaltyLabel})</Typography><Typography sx={{ whiteSpace: 'nowrap', pl: 1 }}>+ {money(summary.noShowPenaltyCents)}</Typography></Stack>
                  <Stack direction="row" justifyContent="space-between"><Typography>Más penalidad por cancelar con menos de {PARTNER_CHANGE_HOURS} h ({summary.lateCancelPenaltyLabel})</Typography><Typography sx={{ whiteSpace: 'nowrap', pl: 1 }}>+ {money(summary.lateCancelPenaltyCents)}</Typography></Stack>
                  <Divider sx={{ my: 1 }} />
                  <Stack direction="row" justifyContent="space-between"><Typography variant="h6">Total a cobrar a la armería</Typography><Typography variant="h6">{money(summary.totalCents)}</Typography></Stack>

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" sx={{ mt: 2 }}>
                    {settlement ? (
                      <>
                        <Chip
                          color={settlementStatus(settlement).color}
                          icon={<Iconify icon={settlement.status === 'paid' ? 'eva:checkmark-circle-2-fill' : 'eva:clock-outline'} />}
                          label={settlement.status === 'paid'
                            ? `Cobrado${settlement.paidAt && settlement.paidAt.toDate ? ` el ${settlement.paidAt.toDate().toLocaleDateString('es-PR')}` : ''}${settlement.paidVia === 'ach' ? ' por ACH' : ''} — ${money(settlement.totalCents)}`
                            : `${settlementStatus(settlement).label}${settlement.dueDate ? ` · vence ${formatDueDate(settlement.dueDate)}` : ''} — ${money(settlement.amountDueCents != null ? settlement.amountDueCents : settlement.totalCents)}`}
                        />
                        {settlement.note && <Typography variant="body2" color="text.secondary">Nota: {settlement.note}</Typography>}
                        {settlement.closedAt && settlement.totalCents !== summary.totalCents && (
                          <Typography variant="caption" color="warning.main">El estado congelado ({money(settlement.totalCents)}) no coincide con las asistencias actuales: recalcúlalo desde «Estados de cuenta».</Typography>
                        )}
                        {canEdit && settlement.status === 'paid' && settlement.paidVia !== 'ach' && !(settlement.payments || []).length && (
                          <Button size="small" color="inherit" onClick={() => markSettled(false)} disabled={settleSaving}>Deshacer</Button>
                        )}
                        <Button size="small" onClick={() => setTab(1)}>Gestionar</Button>
                      </>
                    ) : (
                      canEdit && (
                        <>
                          <TextField size="small" label="Nota (opcional)" placeholder="Ej: cheque #123, ATH Móvil" value={settleNote} onChange={(e) => setSettleNote(e.target.value)} sx={{ minWidth: 280 }} />
                          <Button variant="contained" onClick={() => markSettled(true)} disabled={settleSaving || summary.billableCount === 0} startIcon={<Iconify icon="eva:checkmark-fill" />}>
                            Marcar mes como cobrado
                          </Button>
                        </>
                      )
                    )}
                    <Box sx={{ flex: 1 }} />
                    <Button
                      variant="outlined"
                      onClick={() => openPartnerInvoice({ partner: stPartner, monthKey: stMonth, rows: stRows, settlement })}
                      startIcon={<Iconify icon="eva:printer-outline" />}
                    >
                      Imprimir factura
                    </Button>
                  </Stack>
                </Card>

                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Fecha</TableCell>
                        <TableCell>Cliente</TableCell>
                        <TableCell>Curso</TableCell>
                        <TableCell>Asistencia</TableCell>
                        <TableCell align="right">Precio</TableCell>
                        <TableCell align="right">Comisión</TableCell>
                        <TableCell align="right">Penalidad</TableCell>
                        <TableCell align="right">Se factura</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {stLoading && <TableRow><TableCell colSpan={8}>Cargando...</TableCell></TableRow>}
                      {!stLoading && stRows.length === 0 && <TableRow><TableCell colSpan={8}><Typography color="text.secondary">Sin clientes referidos en {monthLabel(stMonth)}.</Typography></TableCell></TableRow>}
                      {stRows.map((r) => {
                        const st = partnerRowStatus(r);
                        const bill = partnerBillingOf(r);
                        const isPenalty = bill.kind === 'no_show' || bill.kind === 'late_cancel';
                        return (
                          <TableRow key={r.id} hover sx={{ cursor: 'pointer' }} onClick={() => window.open(`/dashboard/reservations/details?id=${r.id}`, '_blank')}>
                            <TableCell>{formatDate(r.date)} {formatTime(r.startTime)}</TableCell>
                            <TableCell>
                              <Typography variant="body2">{r.userName}</Typography>
                              <Typography variant="caption" color="text.secondary">{r.userPhone}{r.licenseNumber ? ` · Lic. ${r.licenseNumber}` : ''}</Typography>
                            </TableCell>
                            <TableCell>
                              {r.categoryName}
                              {(r.courseLanguage || r.licenseType) && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                  {[r.courseLanguage === 'en' ? 'Inglés' : r.courseLanguage === 'es' ? 'Español' : null, r.licenseType === 'renewal' ? 'Renovación' : r.licenseType === 'new' ? 'Licencia nueva' : null].filter(Boolean).join(' · ')}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Chip size="small" label={st.label} color={st.color} variant="outlined" />
                              {typeof r.partnerPenaltyOverride === 'boolean' && (r.status === 'no_show' || r.status === 'cancelled') && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                  {r.partnerPenaltyOverride ? 'Penalidad puesta a mano' : 'Penalidad quitada a mano'}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="right">{money(r.partnerPriceCents)}</TableCell>
                            <TableCell align="right">{bill.kind === 'course' ? `− ${money(bill.commissionCents)}` : '—'}</TableCell>
                            <TableCell align="right">{isPenalty ? money(bill.penaltyCents) : '—'}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, color: isPenalty ? 'error.main' : undefined }}>
                              {bill.kind === 'upcoming' ? 'Pendiente' : money(bill.billedCents)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </Card>
        )}
      </Container>

      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? 'Editar aliado' : 'Nuevo aliado'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {formError && <Alert severity="error">{formError}</Alert>}
            <TextField label="Nombre de la armería *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} fullWidth />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label="Persona de contacto" value={form.contactName} onChange={(e) => setForm({ ...form, contactName: e.target.value })} fullWidth />
              <TextField label="Código" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="Se genera solo" fullWidth />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label="Teléfono (login del portal) *" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} fullWidth placeholder="7875551234" />
              <TextField label={editingId ? 'Nuevo PIN (vacío = no cambiar)' : 'PIN de 4 dígitos *'} value={form.pin} onChange={(e) => setForm({ ...form, pin: e.target.value.replace(/\D/g, '').slice(0, 4) })} fullWidth inputProps={{ inputMode: 'numeric' }} />
            </Stack>
            <TextField label="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} fullWidth />
            <Divider>Precios, comisión y penalidad</Divider>
            <TextField label="Precio general por curso ($)" value={form.defaultPrice} onChange={(e) => setForm({ ...form, defaultPrice: e.target.value })} fullWidth helperText="Se usa en los cursos que no tengan su propio precio abajo" />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label="Comisión por referido ($)" value={form.commission} onChange={(e) => setForm({ ...form, commission: e.target.value })} fullWidth helperText="Se descuenta por cada cliente que asiste" />
              <TextField label="Penalidad ($)" value={form.penalty} onChange={(e) => setForm({ ...form, penalty: e.target.value })} fullWidth helperText={`Por inasistencia o cancelación con menos de ${PARTNER_CHANGE_HOURS} h`} />
            </Stack>
            {courses.length > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>Precio por curso: lo que le cobras a la armería por cada cliente que asiste (vacío = precio general)</Typography>
                <Grid container spacing={1}>
                  {courses.map((c) => (
                    <Grid item xs={12} sm={6} key={c.id}>
                      <TextField size="small" fullWidth label={c.name} value={form.byCategory[c.id] || ''} onChange={(e) => setForm({ ...form, byCategory: { ...form.byCategory, [c.id]: e.target.value } })} placeholder={`Regular: ${money(c.priceNonMemberCents)}`} />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
            <TextField label="Días para pagar después del cierre" value={form.paymentTermDays} onChange={(e) => setForm({ ...form, paymentTermDays: e.target.value.replace(/\D/g, '') })} fullWidth inputProps={{ inputMode: 'numeric' }} helperText="El acuerdo dice 5 (vence el día 5 del mes siguiente). Solo cámbialo con una prórroga por escrito." />
            <TextField label="Notas internas" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} fullWidth multiline rows={2} />
            <FormControlLabel control={<Switch checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />} label="Activo (puede entrar al portal e inscribir)" />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>{saving ? 'Guardando...' : 'Guardar'}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
